import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API } from '../lib/api';
import { toast } from 'sonner';
import { GraduationCap, UploadCloud, CheckCircle2, FileText, Loader2, AlertTriangle, RotateCcw } from 'lucide-react';

const STATUS_STYLE = {
  Pending: { t: '#64748B', b: '#F1F5F9', label: 'Not Submitted' },
  'Not Submitted': { t: '#64748B', b: '#F1F5F9', label: 'Not Submitted' },
  Submitted: { t: '#1D4ED8', b: '#DBEAFE', label: 'Submitted' },
  Uploaded: { t: '#1D4ED8', b: '#DBEAFE', label: 'Submitted' },
  'Under Review': { t: '#D97706', b: '#FEF3C7', label: 'Under Review' },
  Verified: { t: '#16A34A', b: '#DCFCE7', label: 'Verified' },
  Rejected: { t: '#DC2626', b: '#FEE2E2', label: 'Re-upload Required' },
  'Correction Required': { t: '#DC2626', b: '#FEE2E2', label: 'Re-upload Required' },
};

export default function DocumentUpload() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [done, setDone] = useState(false);
  const inputs = useRef({});

  const load = () => {
    setLoading(true);
    fetch(`${API}/public/upload/${token}`)
      .then(async (r) => { if (!r.ok) throw new Error((await r.json()).detail || 'Link error'); return r.json(); })
      .then((d) => { setData(d); setDone(!!d.submitted_at); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, [token]); // eslint-disable-line

  const upload = async (docId, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File is too large. Maximum size is 10 MB.'); return; }
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'jpg', 'jpeg', 'png'].includes(ext)) { toast.error('Only PDF, JPG, JPEG and PNG allowed.'); return; }
    setUploading(docId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API}/public/upload/${token}/documents/${docId}`, { method: 'POST', body: fd });
      if (!r.ok) throw new Error((await r.json()).detail || 'Upload failed');
      setData(await r.json());
      toast.success('Uploaded successfully');
    } catch (e) { toast.error(e.message); }
    finally { setUploading(null); }
  };

  const submitAll = async () => {
    try {
      const r = await fetch(`${API}/public/upload/${token}/submit`, { method: 'POST' });
      if (!r.ok) throw new Error('Submit failed');
      setDone(true);
      toast.success('Documents submitted');
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <Centered><Loader2 className="animate-spin text-navy" size={28} /></Centered>;
  if (error) return (
    <Centered>
      <div className="max-w-sm text-center" data-testid="upload-error">
        <AlertTriangle className="mx-auto text-[#DC2626]" size={34} />
        <p className="mt-3 font-heading text-lg font-bold text-[#172033]">Link unavailable</p>
        <p className="mt-1 text-sm text-[#64748B]">{error}</p>
      </div>
    </Centered>
  );

  return (
    <div className="min-h-screen bg-[#F5F7FB]" data-testid="parent-upload-page">
      <header className="bg-navy px-5 py-5 text-white">
        <div className="mx-auto flex max-w-lg items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gold text-navy"><GraduationCap size={24} /></span>
          <div>
            <p className="font-heading text-base font-bold leading-tight">{data.school_name}</p>
            <p className="text-xs text-gold">Admission Document Submission</p>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-lg space-y-4 p-5">
        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5">
          <p className="text-xs text-[#94A3B8]">Applicant</p>
          <p className="font-heading text-lg font-bold text-[#172033]" data-testid="upload-applicant-name">{data.applicant_name}</p>
          <p className="font-mono text-xs text-action" data-testid="upload-form-number">Form No.: {data.form_number || data.case_number} · {data.class_name}</p>
          <p className="mt-3 rounded-lg bg-[#F8FAFC] p-3 text-sm text-[#475569]">{data.instructions}</p>
        </div>

        {done && (
          <div className="rounded-2xl border-2 border-[#16A34A]/30 bg-[#F0FDF4] p-5 text-center" data-testid="upload-success-banner">
            <CheckCircle2 className="mx-auto text-[#16A34A]" size={30} />
            <p className="mt-2 font-semibold text-[#166534]">Documents submitted successfully. The school office will review your documents.</p>
          </div>
        )}

        <div className="space-y-3">
          {data.documents.map((d) => {
            const s = STATUS_STYLE[d.status] || STATUS_STYLE.Pending;
            const uploaded = d.original_filename && d.status !== 'Pending';
            const needsFix = ['Rejected', 'Correction Required'].includes(d.status);
            return (
              <div key={d.id} className="rounded-xl border border-[#E2E8F0] bg-white p-4" data-testid={`upload-doc-${d.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#172033]">{d.name}{d.required && <span className="ml-1 text-xs text-[#DC2626]">*</span>}</p>
                    {uploaded && <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-[#64748B]"><FileText size={12} /> {d.original_filename}</p>}
                    {needsFix && d.correction_reason && <p className="mt-1 text-xs text-[#DC2626]">⚠ {d.correction_reason}</p>}
                  </div>
                  <span className="shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold" style={{ color: s.t, backgroundColor: s.b }}>{s.label}</span>
                </div>
                {!done && d.status !== 'Verified' && (
                  <label className="mt-3 flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-[#CBD5E1] py-3 text-sm font-semibold text-[#475569] transition hover:border-action hover:text-action">
                    {uploading === d.id ? <Loader2 className="animate-spin" size={15} /> : (needsFix || uploaded ? <RotateCcw size={15} /> : <UploadCloud size={15} />)}
                    {uploading === d.id ? 'Uploading…' : (uploaded ? 'Replace file' : 'Upload')}
                    <input ref={(el) => (inputs.current[d.id] = el)} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden"
                      data-testid={`upload-input-${d.id}`} onChange={(e) => upload(d.id, e.target.files[0])} />
                  </label>
                )}
              </div>
            );
          })}
        </div>

        {!done && (
          <button data-testid="upload-submit-all" onClick={submitAll}
            className="w-full rounded-xl bg-navy py-3.5 text-sm font-semibold text-white transition hover:bg-navy-dark">
            Submit Documents
          </button>
        )}
        <p className="pb-6 text-center text-xs text-[#94A3B8]">Allowed: PDF, JPG, JPEG, PNG · Max 10 MB per file</p>
      </main>
    </div>
  );
}

const Centered = ({ children }) => (
  <div className="flex min-h-screen items-center justify-center bg-[#F5F7FB] p-6">{children}</div>
);
